/**
 * FathomVoyage — Next Cargo Position Intelligence
 * Based on real 2025/2026 trade flow data
 *
 * Sources:
 * - EIA crude tanker route analysis (Jan 2026)
 * - Kpler Q1 2026 tanker market outlook
 * - AXSMarine dry bulk flows 2025
 * - Argus Media / Splash247 market intelligence
 *
 * Structure: for each discharge region + vessel segment,
 * list the most active next loading regions with cargo type,
 * typical market rate context, and rationale.
 *
 * IMPORTANT: This file lives server-side (Cloudflare Worker) in production.
 * Do NOT import directly into frontend components.
 */

export const NEXT_POSITIONS = {

  // ─── After discharging at NORTH EUROPE (Rotterdam / ARA) ──────────────────
  north_europe: {
    vlcc: [
      {
        region: 'arabian_gulf',
        label: 'Arabian Gulf',
        flag: '🇸🇦',
        cargo: 'Crude Oil (VLCC)',
        ports: ['Ras Tanura', 'Kharg Island', 'Basra SPM', 'Ruwais'],
        approxBallastDays: 20,
        marketContext: 'Primary VLCC loading region. Middle East Gulf loadings high with OPEC+ production ramp-up Apr 2026. 45+ VLCC fixtures/week from AG.',
        freightOutlook: 'positive',
        note: 'OPEC+ unwinding cuts — AG volume elevated Q2 2026',
      },
      {
        region: 'west_africa',
        label: 'West Africa',
        flag: '🇳🇬',
        cargo: 'Crude Oil (Suezmax / VLCC)',
        ports: ['Forcados', 'Bonny Terminal', 'Cabinda', 'Girassol FPSO'],
        approxBallastDays: 14,
        marketContext: 'Nigeria and Angola primary export terminals. Suezmax dominant but VLCCs active when WAF/AG arbitrage opens. Guyana (Liza Phase 2) growing.',
        freightOutlook: 'neutral',
        note: 'Nigerian production back to normal Apr 2026',
      },
      {
        region: 'us_gulf',
        label: 'US Gulf',
        flag: '🇺🇸',
        cargo: 'Crude / Clean Products (Suezmax / Aframax)',
        ports: ['Louisiana Offshore Oil Port (LOOP)', 'Corpus Christi', 'Texas City'],
        approxBallastDays: 18,
        marketContext: 'US crude exports growing. WTI/Brent spread determines arbitrage. Strong backhaul opportunity for vessels discharging in Europe.',
        freightOutlook: 'positive',
        note: 'US crude exports to Europe elevated post-Russia sanctions',
      },
    ],
    suezmax: [
      {
        region: 'west_africa',
        label: 'West Africa',
        flag: '🇳🇬',
        cargo: 'Crude Oil',
        ports: ['Forcados', 'Bonny', 'Djeno Terminal', 'Cabinda'],
        approxBallastDays: 14,
        marketContext: 'Traditional Suezmax heartland. Nigeria/Angola programs run 4-5 stems monthly. Guyana Liza Destiny FPSO adding volume.',
        freightOutlook: 'positive',
        note: 'Suezmax demand elevated — VLCCs shifted to AG, creating Suezmax space in WAF',
      },
      {
        region: 'arabian_gulf',
        label: 'Arabian Gulf',
        flag: '🇸🇦',
        cargo: 'Crude Oil',
        ports: ['Ras Tanura', 'Mina Al Ahmadi', 'Fujairah STS'],
        approxBallastDays: 20,
        marketContext: 'For first time in 2 years, Suezmaxes regaining Middle East Gulf market share (Kpler Q1 2026). Indian crude demand from AG elevated.',
        freightOutlook: 'positive',
        note: 'Increased Suezmax share of AG→west loadings in early 2026',
      },
    ],
    capesize: [
      {
        region: 'brazil_ports',
        label: 'Brazil — Iron Ore',
        flag: '🇧🇷',
        cargo: 'Iron Ore (Vale)',
        ports: ['Tubarão / Praia Mole', 'Ponta da Madeira', 'Itaguaí'],
        approxBallastDays: 16,
        marketContext: 'Brazil-China kingmaker for Capesize ton-miles. Vale programs consistent 12 months/year. Low seasonal variation vs Australia. Strong 2025/2026 demand.',
        freightOutlook: 'positive',
        note: 'Brazil anchor for Capesize ton-miles — Brazil→China growing vs Australia→China',
      },
      {
        region: 'australia',
        label: 'Australia — Iron Ore / Coal',
        flag: '🇦🇺',
        cargo: 'Iron Ore / Thermal Coal',
        ports: ['Port Hedland', 'Dampier', 'Newcastle', 'Hay Point'],
        approxBallastDays: 35,
        marketContext: 'Australia 55.77% of global seaborne iron ore exports (2025 YTD). Port Hedland specialises in iron ore. Newcastle leading coal export terminal.',
        freightOutlook: 'neutral',
        note: 'Long ballast from Europe. Typically via Cape or East of Suez. Consider bunker strategy.',
      },
    ],
    panamax: [
      {
        region: 'us_gulf',
        label: 'US Gulf — Grain',
        flag: '🇺🇸',
        cargo: 'Grain (Corn / Soybeans / Wheat)',
        ports: ['New Orleans (Mississippi River)', 'Baton Rouge', 'Houston'],
        approxBallastDays: 18,
        marketContext: 'US Gulf major corn and wheat exporter. US corn to Asia strong. Panamax carry 77.9% of global soybean shipments. US competes with Brazil Apr-Sep.',
        freightOutlook: 'positive',
        note: 'US corn export season peaks Mar-Jul. Strong fixture opportunity.',
      },
      {
        region: 'brazil_ports',
        label: 'Brazil — Grain / Iron Ore',
        flag: '🇧🇷',
        cargo: 'Soybeans / Corn / Iron Ore',
        ports: ['Santos', 'Paranaguá', 'São Luís (Ponta da Madeira)'],
        approxBallastDays: 16,
        marketContext: 'Santos #1 Panamax loading port globally (430+ voyages/month 2025). Record Brazilian soy shipments to China in 2025. Jan-Jun is Brazilian soy season.',
        freightOutlook: 'positive',
        note: 'April is peak Brazilian soybean export season — strong demand',
      },
    ],
  },

  // ─── After discharging at CHINA / KOREA / JAPAN ───────────────────────────
  china_korea: {
    vlcc: [
      {
        region: 'arabian_gulf',
        label: 'Arabian Gulf',
        flag: '🇸🇦',
        cargo: 'Crude Oil',
        ports: ['Ras Tanura', 'Kharg Island (Iran)', 'Mina Al Ahmadi', 'Ruwais'],
        approxBallastDays: 10,
        marketContext: 'Short ballast leg. Over 85% of Hormuz crude exports go to Asian markets. Monthly VLCC loadings from ME expected 160-165 Q2 2026. Very active market.',
        freightOutlook: 'positive',
        note: 'India pivoting from Russian to ME crude — additional AG demand driver',
      },
      {
        region: 'west_africa',
        label: 'West Africa',
        flag: '🇳🇬',
        cargo: 'Crude Oil (VLCC)',
        ports: ['Bonny', 'Forcados', 'Cabinda (Angola)', 'Djeno (Congo)'],
        approxBallastDays: 25,
        marketContext: 'West-to-east arbitrage when WAF grades competitively priced vs AG. Nigerian sweet crude popular with Asian refiners. Longer ballast but higher freight.',
        freightOutlook: 'neutral',
        note: 'Arbitrage dependent — monitor Brent/Dubai spread',
      },
    ],
    capesize: [
      {
        region: 'australia',
        label: 'Australia — Iron Ore',
        flag: '🇦🇺',
        cargo: 'Iron Ore (BHP / Rio Tinto / FMG)',
        ports: ['Port Hedland', 'Dampier', 'Port Walcott'],
        approxBallastDays: 5,
        marketContext: 'Short ballast. Australia 55.77% of global seaborne iron ore. Port Hedland world\'s largest bulk export terminal. Continuous loading programs 365 days.',
        freightOutlook: 'positive',
        note: 'Australia→China/Korea round voyage — most common Capesize trade globally',
      },
      {
        region: 'brazil_ports',
        label: 'Brazil — Iron Ore',
        flag: '🇧🇷',
        cargo: 'Iron Ore (Vale)',
        ports: ['Tubarão', 'Ponta da Madeira', 'Itaguaí'],
        approxBallastDays: 25,
        marketContext: 'Brazil-China is the highest ton-mile Capesize trade globally. Vale stable year-round program. Longer ballast but higher earnings due to distance premium.',
        freightOutlook: 'positive',
        note: 'Brazil→China growing share vs Australia due to grade premium',
      },
      {
        region: 'south_africa',
        label: 'South Africa',
        flag: '🇿🇦',
        cargo: 'Iron Ore / Coal (Richards Bay)',
        ports: ['Saldanha Bay (iron ore)', 'Richards Bay (coal)'],
        approxBallastDays: 20,
        marketContext: 'South Africa to China flexible triangulation opportunity. Richards Bay largest coal export terminal in Africa. Saldanha Bay Sishen iron ore export.',
        freightOutlook: 'neutral',
        note: 'Good ballast triangulation — Cape route ships stopping en route',
      },
    ],
    panamax: [
      {
        region: 'australia',
        label: 'Australia — Coal / Grain',
        flag: '🇦🇺',
        cargo: 'Thermal / Coking Coal · Grain',
        ports: ['Newcastle', 'Hay Point', 'Dalrymple Bay', 'Geraldton'],
        approxBallastDays: 5,
        marketContext: 'Newcastle world\'s largest coal export terminal. 350+ Panamax voyages/month. Strong coal demand from India, Japan, South Korea, Vietnam.',
        freightOutlook: 'positive',
        note: 'Indonesia + Australia dominant coal suppliers to Asia',
      },
      {
        region: 'indonesia',
        label: 'Indonesia — Coal',
        flag: '🇮🇩',
        cargo: 'Thermal Coal',
        ports: ['Samarinda', 'Balikpapan', 'Bontang', 'Muara Jawa'],
        approxBallastDays: 3,
        marketContext: 'Indonesia world\'s largest thermal coal exporter. Short ballast from China/Korea. Indian and Vietnamese import demand strong. Panamax and Supramax dominant.',
        freightOutlook: 'positive',
        note: 'Very short ballast. High frequency cargo. Good repositioning option.',
      },
    ],
  },

  // ─── After discharging at SINGAPORE ──────────────────────────────────────
  singapore: {
    vlcc: [
      {
        region: 'arabian_gulf',
        label: 'Arabian Gulf',
        flag: '🇸🇦',
        cargo: 'Crude Oil',
        ports: ['Ras Tanura', 'Kharg Island', 'Mina Al Ahmadi'],
        approxBallastDays: 8,
        marketContext: 'Short, well-established ballast. AG is the natural reload position from Singapore STS. Most VLCC operators run AG→East Asia round voyages via Singapore.',
        freightOutlook: 'positive',
        note: 'Standard VLCC round voyage hub position',
      },
    ],
    mr: [
      {
        region: 'arabian_gulf',
        label: 'Arabian Gulf — Clean',
        flag: '🇸🇦',
        cargo: 'Clean Petroleum Products',
        ports: ['Jubail', 'Yanbu (Red Sea)', 'Ruwais', 'Jebel Ali'],
        approxBallastDays: 8,
        marketContext: 'Middle East clean product exports to Asia active. Saudi Aramco trading arm (Motiva) large LR/MR shipper. Indian CPP exports also active ex-Sikka/Mundra.',
        freightOutlook: 'positive',
        note: 'ME clean products to Asia — growing trade lane',
      },
      {
        region: 'india',
        label: 'India — CPP Exports',
        flag: '🇮🇳',
        cargo: 'Clean Petroleum Products',
        ports: ['Sikka (Gujarat)', 'Mundra', 'Kandla', 'Vizag'],
        approxBallastDays: 4,
        marketContext: 'Indian refinery complex exports products to Asia and Africa. Reliance Jamnagar world\'s largest refinery complex. Strong MR demand for Indian product exports.',
        freightOutlook: 'positive',
        note: 'Indian CPP exports to East Africa and SE Asia growing',
      },
    ],
  },

  // ─── After discharging at INDIA ──────────────────────────────────────────
  india: {
    vlcc: [
      {
        region: 'arabian_gulf',
        label: 'Arabian Gulf',
        flag: '🇸🇦',
        cargo: 'Crude Oil',
        ports: ['Ras Tanura', 'Kharg Island', 'Basra SPM'],
        approxBallastDays: 3,
        marketContext: 'Very short ballast. India\'s crude imports from AG elevated Apr 2026 following India pivot from Russian crude on US sanctions pressure. 3-4 day ballast.',
        freightOutlook: 'positive',
        note: 'India AG crude imports surging — Indian demand replacing displaced Russian volumes',
      },
    ],
  },

  // ─── After discharging at US GULF ────────────────────────────────────────
  us_gulf: {
    vlcc: [
      {
        region: 'arabian_gulf',
        label: 'Arabian Gulf',
        flag: '🇸🇦',
        cargo: 'Crude Oil',
        ports: ['Ras Tanura', 'Kharg Island', 'Ruwais'],
        approxBallastDays: 22,
        marketContext: 'Long ballast but AG→US Gulf rates historically firm. US refiners active buyers of AG crude when WTI/Brent spread opens. VLCC rates from AG→US Gulf up 118% YoY Nov 2025.',
        freightOutlook: 'positive',
        note: 'Consider West Africa option if AG→USG arbitrage tight',
      },
      {
        region: 'west_africa',
        label: 'West Africa',
        flag: '🇳🇬',
        cargo: 'Crude Oil',
        ports: ['Bonny', 'Forcados', 'Cabinda', 'Girassol'],
        approxBallastDays: 12,
        marketContext: 'Shorter ballast than AG. West Africa to US East Coast historically active. US imports of WAF grades have risen on weaker differentials. Suezmax more common.',
        freightOutlook: 'neutral',
        note: 'US WAF imports recovering after years of domestic shale displacement',
      },
    ],
    panamax: [
      {
        region: 'brazil_ports',
        label: 'Brazil — Grain / Ore',
        flag: '🇧🇷',
        cargo: 'Soybeans / Iron Ore',
        ports: ['Santos', 'Paranaguá', 'Tubarão'],
        approxBallastDays: 10,
        marketContext: 'Triangulation opportunity. After US Gulf discharge, ballast to Brazil for soybean/corn backhaul to Asia. Santos strongest loading port globally for Panamaxes.',
        freightOutlook: 'positive',
        note: 'Classic US Gulf → Brazil → Asia triangle. April is Brazilian soy peak.',
      },
    ],
  },
};

// ─── Region aliases for lookup ────────────────────────────────────────────────
// Maps our discharge regions to NEXT_POSITIONS keys
export const DISCHARGE_TO_POSITION_KEY = {
  north_europe:   'north_europe',
  mediterranean:  'north_europe',   // Similar options to NW Europe
  china_korea:    'china_korea',
  japan:          'china_korea',    // Same options as China/Korea
  singapore:      'singapore',
  india:          'india',
  us_gulf:        'us_gulf',
  us_east_coast:  'us_gulf',       // Similar to US Gulf
  arabian_gulf:   null,            // Just loaded — no next position yet
  west_africa:    'north_europe',  // WAF → discharge typically Europe or Asia
  australia:      'china_korea',   // After Australia discharge, same as China
  red_sea:        null,
  east_africa:    null,
};

// ─── Vessel segment mapping ───────────────────────────────────────────────────
export const VESSEL_TO_SEGMENT = {
  vlcc:     'vlcc',
  suezmax:  'suezmax',
  aframax:  'suezmax',  // Similar options
  mr:       'mr',
  capesize: 'capesize',
  panamax:  'panamax',
  supramax: 'panamax',  // Similar options
  cont8k:   null,       // Containers out of scope
  cont14k:  null,
  lng:      null,       // LNG different market
};

/**
 * Get next cargo position suggestions
 * @param {string} dischargeRegion - destination region from route calculation
 * @param {string} vesselTypeId - vessel type key
 * @returns {Array} sorted list of next position options, or []
 */
export function getNextPositions(dischargeRegion, vesselTypeId) {
  const posKey     = DISCHARGE_TO_POSITION_KEY[dischargeRegion];
  const segmentKey = VESSEL_TO_SEGMENT[vesselTypeId];
  if (!posKey || !segmentKey) return [];
  return NEXT_POSITIONS[posKey]?.[segmentKey] || [];
}
